// ----------------------------------------------------------------------------
// This file contains the thunks related to the media slice
// ----------------------------------------------------------------------------

// Imports ------------------------------------------------------------------

import Event from '@shared/classes/event';
import Pages from '@pages';
import Storage, { TRACK_SETTINGS_KEY } from '@storage';
import { ITrack, TrackType } from '../../types/interfaces/player';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { setAudio, setSubtitles } from '../slices/media.slice';
import { store } from '../store';

// Constants -----------------------------------------------------------------

const MAX_SETTINGS = 10;

// Functions -----------------------------------------------------------------

/**
 * Initializes the tracks for the media slice
 * @param assetId - The asset ID
 * @param thunk - The thunk API
 */
const initTracks = createAsyncThunk('init/tracks', (assetId: string, thunk) => {
  try {
    // If the asset ID exists, check if the tracks are available in the storage
    if (assetId) {
      // Get the track settings from storage
      const rawTrackSettings = Storage.get(TRACK_SETTINGS_KEY);
      // If the track settings exist, parse them
      if (rawTrackSettings) {
        // Parse the track settings
        const trackSettings = JSON.parse(rawTrackSettings);
        // If the track setting exists, set the tracks
        if (trackSettings[assetId]) {
          // If the track setting has audio, set the audio
          if (trackSettings[assetId].tracks.audio) {
            thunk.dispatch(setAudio(trackSettings[assetId].tracks.audio));
          }
          // If the track setting has subtitles, set the subtitles
          if (trackSettings[assetId].tracks.subtitles) {
            thunk.dispatch(
              setSubtitles(trackSettings[assetId].tracks.subtitles)
            );
          }
        }
      }
    }
  } catch (error) {
    thunk.rejectWithValue(error);
  }
});

/**
 * Sets a track in the media slice
 * @param data - The data containing the event and track
 * @param thunk - The thunk API
 */
const setTrack = createAsyncThunk(
  'set/track',
  (data: { event: Event; track: ITrack; type: TrackType }, thunk) => {
    const { event, track, type } = data;
    try {
      // Get the asset ID based on the current page
      const { router } = store.getState();
      const isZapper = router.route.Page === Pages.ZAPPER;
      const assetId = isZapper ? event.channel?.id : event.id;

      // If the asset ID exists, set the track settings
      if (assetId) {
        // Get the track settings from storage
        const rawTrackSettings = Storage.get(TRACK_SETTINGS_KEY);
        // If the track settings exist, parse them
        if (rawTrackSettings) {
          // Get the track settings object
          const trackSettings = JSON.parse(rawTrackSettings);
          // If the track setting exists, update it
          if (trackSettings[assetId]) {
            // Update the track setting based on the type
            if (type === TrackType.AUDIO) {
              trackSettings[assetId].tracks.audio = track?.id ? track : null;
            } else {
              trackSettings[assetId].tracks.subtitles = track?.id
                ? track
                : null;
            }
            // Save the track settings object to storage
            Storage.set(TRACK_SETTINGS_KEY, JSON.stringify(trackSettings));
          }
          // If the track setting does not exist, create it
          else {
            // If the track settings object has MAX_SETTINGS settings, delete the oldest one
            if (Object.keys(trackSettings).length === MAX_SETTINGS) {
              const oldestSetting = Object.keys(trackSettings).sort(
                (a, b) => trackSettings[a].date - trackSettings[b].date
              )[0];
              delete trackSettings[oldestSetting];
            }
            // Create the track setting
            trackSettings[assetId] = {
              date: Date.now(),
              tracks: {
                audio: type === TrackType.AUDIO ? track : null,
                subtitles: type === TrackType.SUBTITLES ? track : null,
              },
            };
            // Save the track settings object to storage
            Storage.set(TRACK_SETTINGS_KEY, JSON.stringify(trackSettings));
          }
        }
        // If the track settings do not exist, create them
        else {
          // Create the track settings object
          const trackSettings = {
            [assetId]: {
              date: Date.now(),
              tracks: {
                audio: type === TrackType.AUDIO ? track : null,
                subtitles: type === TrackType.SUBTITLES ? track : null,
              },
            },
          };
          // Save the track settings object to storage
          Storage.set(TRACK_SETTINGS_KEY, JSON.stringify(trackSettings));
        }
      }

      // Get the action based on the track type
      if (type === TrackType.AUDIO) {
        thunk.dispatch(setAudio(track));
      } else {
        thunk.dispatch(setSubtitles(track));
      }
    } catch (error) {
      thunk.rejectWithValue(error);
    }
  }
);

// Exports -------------------------------------------------------------------

export { initTracks, setTrack };

// ----------------------------------------------------------------------------
